import { useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';

import { Pages } from '../../components/Pages/Pages';
import WithAuthentication from '../../components/WithAuthentication/WithAuthentication';
import { ApplicationState } from '../../domain/types';
import { useAppDispatch } from '../../hooks';
import useApplicationStateRefresh from '../../hooks/useApplicationStateRefresh';
import CoApplicantEmploymentPageDK from '../../pages/EmploymentPage/CoApplicantEmploymentPageDK';
import ErrorPage from '../../pages/ErrorPage/ErrorPage';
import CoApplicantEskatKreditStatusConsentPageDK from '../../pages/EskatKreditStatusPage/CoApplicantEskatKreditStatusConsentPageDK';
import CoApplicantFinancePageDK from '../../pages/FinancePage/CoApplicantFinancePageDK';
import CoApplicantHowToApplyPage from '../../pages/HowToApplyPage/CoApplicantHowToApplyPage';
import CoApplicantKycOngoingPage from '../../pages/KycPage/CoApplicantKycOngoingPage';
import CoApplicantKycPage from '../../pages/KycPage/CoApplicantKycPage';
import CoApplicantLivingConditionPage from '../../pages/LivingConditionPage/CoApplicantLivingConditionPage';
import CoApplicantSignAgreementPage from '../../pages/SignAgreementPage/CoApplicantSignAgreementPage';
import OngoingStaticPage from '../../pages/StatusPage/OngoingStaticPage';
import CoApplicantSubmitPageDK from '../../pages/SubmitPage/CoApplicantSubmitPageDK';
import { selectAccessToken } from '../../store/authentication';
import { setCurrentPage } from '../../store/wizard/wizard';

/**
 * The DK co-applicant's own journey, entered from the invite URL.
 *
 * DK deliberately does not reuse NO's `CoApplicantLogInPage` -- that's NO's SBL income-verification step, not
 * a login page; DK verifies income through eSkat/KreditStatus instead.
 *
 * Mirrors NO's `!accessToken` gate below: `CoApplicantHowToApplyPage` and `CoApplicantEskatKreditStatusConsentPageDK`
 * render outside `WithAuthentication` while unauthenticated, so the co-applicant can read both before MitID
 * runs. The latter is what triggers `initAuthentication()`, via its own `mitid-login` action. Both pages repeat
 * at the front of the authenticated `CO_SUBMIT` list so `currentPage` stays meaningful across that transition.
 *
 * `CoApplicantFinancePageDK` is a DK-specific fork, not the shared `CoApplicantIncomePage` NO uses: the
 * `cl-common` PCN-620 rescope moved `coapplicantYearlyGrossIncome`/`coapplicantMonthlyIncomeOther` (what that
 * shared page collects) into NO's own exclusive contract block and added `coapplicantNetMonthlyIncome`/
 * `coapplicantNewDebtThisYear` for DK instead. DK also no longer has an Accommodation or Expense step --
 * `coapplicantHabitationForm` was deleted from the contract outright, and the old Expense fields moved to
 * NO-only alongside the income ones.
 */
const SubmitCoApplicantWizardDK = () => {
    const dispatch = useAppDispatch();
    // Matches SubmitCoApplicantWizardNO's identical gate exactly -- no demo-mode fold-in (LP-2946 removed
    // the getDemoAccessToken bypass this used to rely on). Accepted consequence: DK's demo-mode co-applicant
    // sessions no longer skip straight past CO_SUBMIT's eSkat/KreditStatus consent gate; they now need a
    // MitID login round trip (mocked via oauthHandlers.ts, LP-2947) to complete first, exactly like NO's SBL
    // flow already requires today.
    const accessToken = useSelector(selectAccessToken);
    const { isError, applicationState } = useApplicationStateRefresh();

    // These two states each render a short, single-purpose page list, so the index must return to 0 on entry
    // -- otherwise `useDefaultPagesAction` keeps counting from wherever CO_SUBMIT left off. Deliberately does
    // not reset on entry to CO_SUBMIT itself (matching NO): CO_SUBMIT is first reached via the unauthenticated
    // `getPage` branch below, and resetting the moment `applicationState` resolves to it (right after login)
    // would discard that pre-login progress. `useLayoutEffect` so the store converges before the browser paints.
    useLayoutEffect(() => {
        if (applicationState === 'KYC_CO_APPLICANT' || applicationState === 'SIGN_AGREEMENT') {
            dispatch(setCurrentPage(0));
        }
    }, [dispatch, applicationState]);

    if (isError) {
        return <ErrorPage />;
    }

    const getPage = () => {
        if (!accessToken) {
            return (
                <Pages>
                    <CoApplicantHowToApplyPage />
                    <CoApplicantEskatKreditStatusConsentPageDK />
                </Pages>
            );
        }

        switch (applicationState) {
            case 'CO_SUBMIT':
                return (
                    <WithAuthentication>
                        <Pages>
                            <CoApplicantHowToApplyPage />
                            <CoApplicantEskatKreditStatusConsentPageDK />
                            <CoApplicantLivingConditionPage />
                            <CoApplicantEmploymentPageDK />
                            <CoApplicantFinancePageDK />
                            <CoApplicantKycPage />
                            <CoApplicantSubmitPageDK />
                        </Pages>
                    </WithAuthentication>
                );

            case 'KYC_CO_APPLICANT':
                return (
                    <WithAuthentication>
                        <Pages>
                            <CoApplicantKycOngoingPage />
                        </Pages>
                    </WithAuthentication>
                );

            case 'SIGN_AGREEMENT':
                return (
                    <WithAuthentication>
                        <Pages>
                            <CoApplicantSignAgreementPage />
                        </Pages>
                    </WithAuthentication>
                );

            default:
                return (
                    <WithAuthentication>
                        <OngoingStaticPage applicationState={applicationState as ApplicationState} />
                    </WithAuthentication>
                );
        }
    };

    return <>{getPage()}</>;
};

export default SubmitCoApplicantWizardDK;
